import { useMemo } from 'react';

import { Dimensions, Metrics } from 'components/insights/constants';
import {
  formatDaysValue,
  formatDecimal,
  formatPercentage,
  formatResponseTime,
  formatWholeNumber,
} from 'components/insights/formatting';
import { useNetworkPerformanceStore } from 'stores/networkPerformanceStore';

import { readGrandTotal, readGrandTotalOrZero, sumByDimensionValue } from '../../shared/slideout/rollupHelpers';
import { StatTile, TilesGrid } from '../../shared/slideout/statTiles';

import { type EntitySlideoutAnalyticsResult } from './useEntitySlideoutAnalytics';

type Props = EntitySlideoutAnalyticsResult;

export default function EntitySlideoutTiles({
  dischargeRollups,
  careTransitionsRollups,
  serviceRefusalsRollups,
  safeReturnsRollups,
  loading,
}: Props) {
  const selectedGroupType = useNetworkPerformanceStore((s) => s.selectedGroupType);

  // Discharge metrics (grand totals)
  const totalDischarges = readGrandTotalOrZero(dischargeRollups, Metrics.ID_COUNT);
  const readmissionRate = readGrandTotal(dischargeRollups, Metrics.READMISSION_RATE);
  const readmissionsSum = readGrandTotalOrZero(dischargeRollups, Metrics.READMISSIONS_SUM);
  const oes = readGrandTotal(dischargeRollups, Metrics.OLIO_ENGAGEMENT_SCORE);
  const engagements = readGrandTotal(dischargeRollups, Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK);

  // Care transitions
  const avgDaysToStartCare = readGrandTotal(careTransitionsRollups, Metrics.DAYS_TO_START_CARE_AVERAGE);
  const acceptedReferrals = readGrandTotalOrZero(careTransitionsRollups, Metrics.LOCATION_EPISODE_ID_COUNT);

  // Service refusals (total + by-type breakdown)
  const totalRefusals = readGrandTotalOrZero(serviceRefusalsRollups, Metrics.SERVICE_REFUSALS_COUNT);

  const refusedByBreakdown = useMemo(() => {
    // Service refusals query has dimensions [GROUP, SERVICE_REFUSAL_REFUSED_BY_TYPE].
    // Read the grand total rollup for the refused-by-type breakdown from the detail rows.
    const { analyticsResponse } = serviceRefusalsRollups;
    const typeIdx = analyticsResponse.findDimensionIndex(Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE);
    if (typeIdx < 0) return { patientCount: 0, facilityCount: 0 };

    const types = analyticsResponse.responseData
      .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
      .map((row) => String(row.dimensionValues[typeIdx]?.value ?? ''));

    const metricIdx = analyticsResponse.findMetricIndex(Metrics.SERVICE_REFUSALS_COUNT);
    const counts = analyticsResponse.responseData
      .filter((row) => (row.grouping ?? 0) === 0 && (row.datesIndex ?? 0) === 0)
      .map((row) => (metricIdx >= 0 ? Number(row.metricValues[metricIdx]?.value ?? 0) : 0));

    const byType = sumByDimensionValue(types, counts);
    return {
      patientCount: byType.get('patient') ?? 0,
      facilityCount: byType.get('rehab_facility') ?? 0,
    };
  }, [serviceRefusalsRollups]);

  // Safe returns
  const safeReturnsRequested = readGrandTotalOrZero(safeReturnsRollups, Metrics.SAFE_RETURNS_REQUESTED_COUNT);
  const avgResponseTime = readGrandTotal(safeReturnsRollups, Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE);

  return (
    <TilesGrid>
      <StatTile
        label='Avg. Discharge to Admission Time'
        loading={loading}
        primary={formatDaysValue(avgDaysToStartCare)}
        addendum={`Based on patient volume of ${formatWholeNumber(acceptedReferrals)}`}
      />
      <StatTile
        label='Service Refusals'
        loading={loading}
        primary={formatWholeNumber(totalRefusals)}
        addendum={`${formatWholeNumber(refusedByBreakdown.patientCount)} by patient | ${formatWholeNumber(refusedByBreakdown.facilityCount)} by ${selectedGroupType!.displayName}`}
      />
      <StatTile
        label='Safe Returns'
        loading={loading}
        primary={formatWholeNumber(safeReturnsRequested)}
        addendum={
          safeReturnsRequested === 0
            ? 'No avg. response time'
            : `Avg. response time: ${formatResponseTime(avgResponseTime)}`
        }
      />
      <StatTile
        label='Olio Engagement Score (OES)'
        loading={loading}
        primary={formatPercentage(oes, 0)}
        addendum='Goal: 100%'
      />
      <StatTile
        label='Engagements Per Patient Per Week'
        loading={loading}
        primary={formatDecimal(engagements)}
        addendum={`Based on ${formatWholeNumber(totalDischarges)} patients`}
      />
      <StatTile
        label='Readmission Rate (%)'
        loading={loading}
        primary={formatPercentage(readmissionRate)}
        addendum={`${formatWholeNumber(readmissionsSum)} of ${formatWholeNumber(totalDischarges)} total discharges`}
      />
    </TilesGrid>
  );
}
